import Select from '../../generic/Select/Select';
import { FormLabel, LabeledTextInput } from '../../generic/Forms';
import { ModalAddDependencyAlert } from './ModalAddDependencyAlert';
import { FETCHER_DROPDOWN_OPTIONS, commonLinuxArchs, isLinuxFetcher, linuxDistrosByFetcher } from '../../../../shared/fetchers';
import type { LocatorModalData } from './types';

type AddLocatorDependencyProps = {
  onChange: (dep: Partial<LocatorModalData>) => void;
  dependencyToAdd: Partial<LocatorModalData>;
  isDisabled: boolean;
};

const selectStyles = {
  control: (base: Record<string, unknown>) => ({
    ...base,
    height: 43,
    minHeight: 43,
  }),
};

const AddLocatorDependency = ({ isDisabled, onChange, dependencyToAdd }: AddLocatorDependencyProps) => {
  const onAttributeChange = (attr: keyof LocatorModalData | 'error', value: string) => {
    const changes: Partial<LocatorModalData> = {
      ...dependencyToAdd,
      kind: 'locator',
      [attr]: value,
    };

    if (attr === 'fetcher') {
      changes.distro = undefined;
    }

    onChange(changes);
  };

  return (
    <div className="locator-panel">
      <p className="locator-panel__title">
        Search for your packages across the web
      </p>
      <ModalAddDependencyAlert dependencyToAdd={dependencyToAdd} />
      <div className="locator-panel__body">
        <div className="locator-panel__body__section locator-panel__body__name-version">
          <FormLabel
            label="Package manager"
            requiredText="(Required)"
          >
            <Select
              id="fetcher"
              placeholder="Select package manager..."
              options={FETCHER_DROPDOWN_OPTIONS}
              isDisabled={isDisabled}
              value={FETCHER_DROPDOWN_OPTIONS.find((opt) => opt.value === dependencyToAdd.fetcher)}
              onChange={(e) => {
                if (e) {
                  onAttributeChange('fetcher', e.value);
                }
              }}
              styles={selectStyles}
            />
          </FormLabel>
        </div>
        <div className="locator-panel__body__section locator-panel__body__name-version">
          <LabeledTextInput
            type="text"
            label="Name"
            value={dependencyToAdd.packageName || ''}
            placeholder="dependency"
            isRequired
            isDisabled={isDisabled}
            onChange={n => onAttributeChange('packageName', n)}
          />
          <LabeledTextInput
            type="text"
            label="Revision"
            value={dependencyToAdd.revision || ''}
            placeholder="1.2.3"
            isRequired
            isDisabled={isDisabled}
            onChange={r => onAttributeChange('revision', r)}
          />
        </div>
        { isLinuxFetcher(dependencyToAdd.fetcher) &&
          <div className="locator-panel__body__section locator-panel__body__name-version">
            <FormLabel
              label="Distro"
              requiredText="(Required)"
            >
              <Select
                isCreatable
                id="distro"
                placeholder="Select linux distribution..."
                options={linuxDistrosByFetcher[dependencyToAdd.fetcher]}
                isDisabled={isDisabled}
                value={{
                  value: dependencyToAdd.distro,
                  label: linuxDistrosByFetcher[dependencyToAdd.fetcher]
                    .find((opt) => opt.value === dependencyToAdd.distro)?.label ?? dependencyToAdd.distro,
                }}
                onChange={(e) => {
                  if (e && e.value) {
                    onAttributeChange('distro', e.value);
                  }
                }}
                styles={selectStyles}
              />
            </FormLabel>
            <LabeledTextInput
              type="text"
              label="Release"
              value={dependencyToAdd.distroRelease || ''}
              placeholder="23.10"
              isRequired
              isDisabled={isDisabled}
              onChange={r => onAttributeChange('distroRelease', r)}
            />
            <FormLabel
              label="Architecture"
              requiredText="(Required)"
            >
              <Select
                isCreatable
                id="arch"
                placeholder="Select CPU architecture..."
                options={commonLinuxArchs.map(a => ({ value: a, label: a }))}
                isDisabled={isDisabled}
                value={{
                  value: dependencyToAdd.arch,
                  label: dependencyToAdd.arch,
                }}
                onChange={(e) => {
                  if (e && e.value) {
                    onAttributeChange('arch', e.value);
                  }
                }}
                styles={selectStyles}
              />
            </FormLabel>
          </div>}
      </div>
    </div>
  );
};

export default AddLocatorDependency;
